// src/components/common/DebugPanel.js
import React, { useState, useCallback } from 'react';
import { 
  Paper, 
  Typography, 
  Box, 
  Tabs, 
  Tab,
  Divider,
  IconButton
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import RefreshIcon from '@mui/icons-material/Refresh';
import CodeIcon from '@mui/icons-material/Code';
import DebugHelper from './DebugHelper';
import apiClient from '../../services/EnhancedApiClient';

const DebugPanel = () => {
  const [tabValue, setTabValue] = useState(0);
  const [statsData, setStatsData] = useState(null);
  const [fraudAlertsData, setFraudAlertsData] = useState(null);
  const [cardDeclinesData, setCardDeclinesData] = useState(null);
  const [cardHistoryData, setCardHistoryData] = useState(null);
  const [configData, setConfigData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const fetchAllData = useCallback(async () => {
    setRefreshing(true);
    try {
      // Fetch all data types in parallel
      const [stats, alerts, declines, history, config] = await Promise.all([
        apiClient.fetchStats(true),
        apiClient.fetchFraudAlerts({ force: true }),
        apiClient.fetchCardDeclines(true),
        apiClient.fetchCardDeclineHistory(true),
        apiClient.getConfig()
      ]);
      
      setStatsData(stats);
      setFraudAlertsData(alerts);
      setCardDeclinesData(declines);
      setCardHistoryData(history);
      setConfigData(config);
    } catch (error) {
      console.error('Error fetching debug data:', error);
    } finally {
      setRefreshing(false);
    }
  }, []);

  // Fetch data on first render
  React.useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  return (
    <Paper sx={{ p: 3, mb: 3, position: 'relative' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center">
          <BugReportIcon sx={{ mr: 1, color: 'error.main' }} />
          <Typography variant="h5" component="h2">Debug Panel</Typography>
        </Box>
        <IconButton 
          onClick={fetchAllData} 
          disabled={refreshing}
          color="primary"
          title="Refresh debug data"
        >
          <RefreshIcon />
        </IconButton>
      </Box>
      
      <Divider sx={{ mb: 2 }} />
      
      <Tabs 
        value={tabValue} 
        onChange={handleTabChange} 
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2 }}
      >
        <Tab icon={<CodeIcon />} iconPosition="start" label="API Client" />
        <Tab icon={<CodeIcon />} iconPosition="start" label="Statistics" />
        <Tab icon={<CodeIcon />} iconPosition="start" label="Fraud Alerts" />
        <Tab icon={<CodeIcon />} iconPosition="start" label="Card Declines" />
        <Tab icon={<CodeIcon />} iconPosition="start" label="Decline History" />
        <Tab icon={<CodeIcon />} iconPosition="start" label="Config" />
      </Tabs>
      
      {/* API Client Tab */}
      {tabValue === 0 && (
        <DebugHelper 
          data={apiClient} 
          title="API Client Instance" 
          initialExpanded={true}
          prettify={false}
        />
      )}
      
      {/* Statistics Tab */}
      {tabValue === 1 && (
        <DebugHelper 
          data={statsData} 
          title="Statistics Data" 
          initialExpanded={true}
        />
      )}
      
      {/* Fraud Alerts Tab */}
      {tabValue === 2 && (
        <DebugHelper 
          data={fraudAlertsData} 
          title="Fraud Alerts Data" 
          initialExpanded={true}
        />
      )}
      
      {/* Card Declines Tab */}
      {tabValue === 3 && (
        <DebugHelper 
          data={cardDeclinesData} 
          title="Card Declines Data" 
          initialExpanded={true}
        />
      )}
      
      {/* Decline History Tab */}
      {tabValue === 4 && (
        <DebugHelper 
          data={cardHistoryData} 
          title="Card Decline History Data" 
          initialExpanded={true}
        />
      )}
      
      {/* Config Tab */}
      {tabValue === 5 && (
        <DebugHelper 
          data={configData} 
          title="System Configuration" 
          initialExpanded={true}
        />
      )}
    </Paper>
  );
};

export default DebugPanel;